import { supabase } from "../lib/supabase";

/**
 * One printing/color variant of a card.
 *
 * FAB cards can have up to four pitch variants (red/yellow/blue/purple) with
 * different stats and, for some cards, different functional text. The DB stores
 * one row per variant under the same `name`; `unique_id` identifies a printing.
 */
export interface CardVariant {
  uniqueId: string;
  name: string;
  color: string | null;
  pitch: string | null;
  cost: string | null;
  power: string | null;
  defense: string | null;
  typeText: string | null;
  text: string | null;
  /** Lowest rarity across the card's printings (name-level), or null. */
  rarity: string | null;
}

interface CardRow {
  unique_id: string;
  name: string;
  color: string | null;
  pitch: string | null;
  cost: string | null;
  power: string | null;
  defense: string | null;
  type_text: string | null;
  functional_text_plain: string | null;
  rarity: string | null;
}

const CARD_COLUMNS =
  "unique_id,name,color,pitch,cost,power,defense,type_text,functional_text_plain,rarity";

/** Rarity tiers, lowest first. Basic and Token share the lowest tier; Promo and
 * Marvel are ranked last so they only apply without a regular printing. */
const RARITY_ORDER = ["B", "T", "C", "R", "S", "M", "L", "F", "P", "V"];

function lowestRarity(codes: Array<string | null>): string | null {
  let best: string | null = null;
  let bestRank = Infinity;
  for (const code of codes) {
    if (!code) continue;
    const rank = RARITY_ORDER.indexOf(code);
    if (rank === -1) continue;
    if (rank < bestRank) {
      bestRank = rank;
      best = code;
    }
  }
  return best;
}

/** Session cache: lowercased card name -> fetched variants. */
const cache = new Map<string, CardVariant[]>();
/** In-flight loads, so concurrent taps on one card share a single request. */
const inflight = new Map<string, Promise<CardVariant[]>>();

/** Variants already fetched this session, or null if the card is not cached. */
export function getCachedCardVariants(name: string): CardVariant[] | null {
  return cache.get(name.toLowerCase()) ?? null;
}

/**
 * Load every color variant for a card name, ordered by pitch (red → yellow →
 * blue → purple, then colorless). Matching is case-insensitive, mirroring the
 * judge's entity parser. Identical printings of the same color are collapsed.
 *
 * Results are cached for the session, and concurrent callers share one request,
 * so a card is fetched at most once.
 */
export function fetchCardVariants(name: string): Promise<CardVariant[]> {
  const key = name.toLowerCase();

  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);

  const pending = inflight.get(key);
  if (pending) return pending;

  const request = loadCardVariants(name)
    .then((variants) => {
      cache.set(key, variants);
      inflight.delete(key);
      return variants;
    })
    .catch((error: unknown) => {
      inflight.delete(key);
      throw error;
    });

  inflight.set(key, request);
  return request;
}

async function loadCardVariants(name: string): Promise<CardVariant[]> {
  const { data, error } = await supabase
    .from("cards")
    .select(CARD_COLUMNS)
    .ilike("name", name)
    .order("pitch", { ascending: true });
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as CardRow[];
  const rarity = lowestRarity(rows.map((row) => row.rarity));
  const seen = new Set<string>();
  const variants: CardVariant[] = [];
  for (const row of rows) {
    const key = [
      row.color,
      row.pitch,
      row.cost,
      row.power,
      row.defense,
      row.functional_text_plain,
    ].join("|");
    if (seen.has(key)) continue;
    seen.add(key);
    variants.push({
      uniqueId: row.unique_id,
      name: row.name,
      color: row.color,
      pitch: row.pitch,
      cost: row.cost,
      power: row.power,
      defense: row.defense,
      typeText: row.type_text,
      text: row.functional_text_plain,
      rarity,
    });
  }
  return variants;
}
